/**
 * Debug utility to test image fetching and validate blob data
 * Run this in browser console to diagnose issues
 */

export async function debugImageFetch(url: string) {

  try {
    // Step 1: Fetch the image
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Step 2: Get blob
    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error("Blob is empty (0 bytes)!");
    }

    // Step 3: Validate blob data
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Step 4: Determine correct MIME type from magic bytes
    let mimeType = blob.type;

    // Check magic bytes (file signature)
    if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
      mimeType = 'image/png';
    } else if (uint8Array[0] === 0xFF && uint8Array[1] === 0xD8 && uint8Array[2] === 0xFF) {
      mimeType = 'image/jpeg';
    } else if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46 && uint8Array[3] === 0x46) {
      mimeType = 'image/webp';
    } else if (!mimeType || mimeType === 'application/octet-stream') {
      // Fallback to URL extension
      const urlLower = url.toLowerCase();
      if (urlLower.endsWith('.png')) {
        mimeType = 'image/png';
      } else if (urlLower.match(/\.jpe?g$/)) {
        mimeType = 'image/jpeg';
      } else if (urlLower.endsWith('.webp')) {
        mimeType = 'image/webp';
      }
    }


    // Step 5: Create proper blob with correct type
    const correctedBlob = new Blob([uint8Array], { type: mimeType });

    // Step 6: Create File
    const fileName = url.split('/').pop() || 'image.png';
    const file = new File([correctedBlob], fileName, {
      type: mimeType,
      lastModified: Date.now()
    });

    // Step 7: Create preview URL
    const objectUrl = URL.createObjectURL(file);

    return {
      blob: correctedBlob,
      file,
      objectUrl,
      mimeType
    };

  } catch (error: any) {
    throw error;
  }
}

// Make it available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).debugImageFetch = debugImageFetch;
}
